/**
 * usePropertyNameLookup — the property slice's thin name-lookup hook
 * (SCRUM-334 Step 6).
 *
 * A DELIBERATE, minimal cross-slice read surface: the booking
 * ReservationsGrid needs a `propertyId → displayName` map to render the
 * "guest · property" sub-line without teaching the booking BC how to
 * fetch or display property copy (BC boundary honesty — booking does not
 * own property display names). The grid consumes this via the sanctioned
 * property barrel export and falls back to a short `propertyId` when a
 * name is not yet resolved (mirrors the property slice's owner-name
 * resolve-or-fallback).
 *
 * This hook reads the SAME endpoint `useProperties()` reads
 * (`GET /organizations/{slug}/properties`) and reuses the property
 * slice's status idiom (loading / ready / forbidden / error), but it
 * projects ONLY what the grid needs: a `ReadonlyMap<propertyId, name>`.
 * It deliberately does NOT run rows through the full `projectPropertyDto`
 * (which decodes address / capacity / status for the list & detail
 * screens) — a name lookup needs only the id and the display name, so it
 * projects `id → (websiteNameOverride ?? unitName)` straight off the DTO.
 * That keeps the lookup resilient (a row missing the heavier fields still
 * yields a name), light (no unused decode work), and matches the display
 * name precedence used everywhere the property is referenced: the
 * internal-friendly `websiteNameOverride` wins over the formal `unitName`
 * when set.
 *
 * A 403 is passed through as `forbidden` (the caller lost `property.read`
 * since `/auth/me` cached) and the grid surfaces the same
 * permission-denied affordance rather than a broken name column; any
 * other non-ok status is `error`.
 */

import * as React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';

type PropertyResponseDto = components['schemas']['PropertyResponseDto'];

/**
 * Internal sentinel for the 403 branch — same idiom as
 * `use-properties-list.ts`. The queryFn returns this symbol on 403 so we
 * can distinguish forbidden from a generic error in the status union.
 */
const FORBIDDEN = Symbol('property-name-lookup:forbidden');

/**
 * The closed status union — mirrors `UsePropertiesResult` but exposes
 * ONLY the projected `nameById` map on `ready` (never the full
 * `PropertyView[]`; the grid needs names, not the property surface). The
 * `loading` / `forbidden` / `error` branches carry no map — the caller
 * branches on `status` exactly as it does for `useReservationsList`.
 */
export type UsePropertyNameLookupResult =
  | {
      readonly status: 'loading';
      readonly nameById?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** `propertyId → display name`. `.get(unknownId)` is `undefined`. */
      readonly nameById: ReadonlyMap<string, string>;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly nameById?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly nameById?: undefined;
      readonly refetch: () => void;
    };

/**
 * The display name for a property row: `websiteNameOverride` (the
 * internal-friendly override the FE surfaces as `nickname`) when set,
 * else the formal `unitName`. Matches the precedence used everywhere the
 * property is referenced.
 */
function propertyDisplayName(dto: PropertyResponseDto): string {
  const override = dto.websiteNameOverride;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return dto.unitName;
}

export function usePropertyNameLookup(): UsePropertyNameLookupResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    // Rooted at the property list namespace + a `name-lookup` tag so this
    // projection-specific cache entry is distinct from the full list
    // hook's entry (different selector) yet still invalidates under the
    // property root on any property mutation.
    queryKey: [
      ...propertyQueryKeys.lists(),
      'name-lookup',
      orgSlug,
    ] as const,
    enabled: orgSlug !== null,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/properties', {
        // Include archived properties: a preserved (archived) property can still
        // own historical reservations, and those rows must resolve to a real
        // name rather than a UUID fragment (codex F-2). The list endpoint
        // defaults to excluding archived, so request them explicitly.
        params: {
          path: { slug: orgSlug as string },
          query: { includeArchived: true },
        },
      });
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  // Project `PropertyResponseDto[] → Map(id → name)` only when ready.
  // Memoised on the query data reference so the map is stable across
  // re-renders that don't change the underlying rows.
  const nameById = React.useMemo<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>();
    const data = query.data;
    if (data === undefined || data === FORBIDDEN) {
      return map;
    }
    for (const dto of data.data) {
      map.set(dto.id, propertyDisplayName(dto));
    }
    return map;
  }, [query.data]);

  if (orgSlug === null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  return { status: 'ready', nameById, refetch };
}
